/**
 * Created by sammy on 1/21/20.
 * Project: webapp-template. File: AppStates
 */

import { AuthService } from "../auth/AuthService";
import { observer, useLocalObservable, useLocalStore } from "mobx-react-lite";
import React, { createContext, useContext } from "react";
// import {ServerAPI} from '../core/ServerAPI';
import { AppGlobalService } from "./AppGlobalService";
import { ProgressService } from "../core/progress/ProgressService";
import { DialogService } from "../core/dialog/DialogService";
import { GRPCServices } from "../grpc/grpcapi";
import { ProjectService } from "../project/ProjectService";
import { DeploymentService } from "../deployment/DeploymentService";
import { GalaxyMigrateDeploymentService } from "../galaxymigrate/GmDeploymentService";
import { GalaxyMigrateMigrationService } from "../migration/GmMigrationService";
import { configure } from "mobx";
import { ProjectManagementService } from "../management/ProjectManagementService";
import { AppUpdateCheckerService } from "../core/updateChecker/AppUpdateCheckerService";
import { NotificationService } from "../../common/notifications/NotificationService";
import { UserService } from "../user/UserService";
import { HelpService } from "../help/HelpService";
import { IntegrationsService } from "../integrations/IntegrationsService";
import { PhoenixDeploymentService } from "../protection/servers/PhoenixDeploymentService";
import { CdmPolicyService } from "../protection/policies/CdmPolicyService";
import { CirrusProtectDeploymentService } from "../protection/hosts/CirrusProtectDeploymentService";
import { ChecklistService } from "../migration/sessionDetails/checklist/ChecklistService";
import { LicenseService } from "../license/LicenseService";
import { PrivateEditionService } from "../privateEdition/PrivateEditionService";
import { SupportService } from "../support/SupportService";
import { ReportService } from "../reports/ReportService";

export const configureMobx = () => {
    configure({
        enforceActions: "never", // currently set to never (not recommended) because of async function support. believe that it is okay to not have to wrap state changes as long as it is from the same class
    });
};

const initServices = () => {
    // const api                         = new ServerAPI();
    const api = new GRPCServices();
    const appGlobalService = new AppGlobalService(api);
    const dialogService = new DialogService();
    const notificationService = new NotificationService();
    const progressService = new ProgressService(dialogService);
    const appUpdateCheckerService = new AppUpdateCheckerService(appGlobalService, dialogService, notificationService);
    const authService = new AuthService(api, dialogService);
    const userService = new UserService(api, authService, dialogService, progressService);
    const projectService = new ProjectService(api, dialogService, progressService);
    const projectManagementService = new ProjectManagementService(api, dialogService, progressService, projectService);
    const deploymentService = new DeploymentService(api, projectService, dialogService, progressService);
    const gmDeploymentService = new GalaxyMigrateDeploymentService(api, projectService, dialogService, progressService, deploymentService);
    const gmMigrationService = new GalaxyMigrateMigrationService(api, dialogService, progressService, gmDeploymentService, deploymentService, projectService);
    const helpService = new HelpService(api, dialogService, progressService);
    const integrationsService = new IntegrationsService(api, dialogService, progressService, projectService);
    const phoenixDeploymentService = new PhoenixDeploymentService(api, projectService, dialogService, progressService);
    const cdmPolicyService = new CdmPolicyService(api, projectService, dialogService, progressService);
    const cpDeploymentService = new CirrusProtectDeploymentService(api, projectService, deploymentService, dialogService, progressService);
    const privateEditionService = new PrivateEditionService(api, dialogService, progressService);
    const checklistService = new ChecklistService(api, dialogService, progressService);
    const licenseService = new LicenseService(api, dialogService, progressService, projectService);
    const supportService = new SupportService(api, dialogService, progressService);
    const reportService = new ReportService(api, dialogService, progressService);

    return {
        appGlobalService,
        progressService,
        dialogService,
        notificationService,
        api,
        appUpdateCheckerService,
        authService,
        projectService,
        projectManagementService,
        deploymentService,
        gmDeploymentService,
        gmMigrationService,
        userService,
        helpService,
        integrationsService,
        phoenixDeploymentService,
        cdmPolicyService,
        cpDeploymentService,
        checklistService,
        licenseService,
        privateEditionService,
        supportService,
        reportService,
        // migrationSourceService, migrationDestinationService,migrationService,systemService,
    };
};

export type AppServicesType = ReturnType<typeof initServices>;
// use partial because of mocking
export const appStateContext = createContext<Partial<AppServicesType> | null>(null);

export const useAppServices = () => {
    const services = useContext(appStateContext);
    if (!services) {
        throw new Error("not inside app services provider");
    }
    return services;
};

// ======================
// AppServicesProvider
// ======================
interface AppServicesProviderProps {
    children: React.ReactNode;
}

export const AppServicesProvider: React.FC<AppServicesProviderProps> = observer(({ ...p }) => {
    const store = useLocalObservable(initServices);
    return <appStateContext.Provider value={store}>{p.children}</appStateContext.Provider>;
});
